// src/components/communications/CallItem.js
import React, { useState } from 'react';
import styled from 'styled-components';
import { 
  FaPhone, 
  FaPhoneSlash, 
  FaLongArrowAltRight, 
  FaLongArrowAltLeft, 
  FaPlay, 
  FaPause,
  FaEdit,
  FaSave,
  FaTimes
} from 'react-icons/fa';
import { formatDateTime } from '../../modules/communications/utils/formatUtils';
import { updateCallNotes } from '../../services/communicationService';
import TranscriptionSection from './TranscriptionSection';

const ItemContainer = styled.div`
  background-color: ${props => props.theme.colors.surface};
  border-radius: ${props => props.theme.borderRadius};
  padding: ${props => props.theme.spacing(2)};
  margin-bottom: ${props => props.theme.spacing(2)};
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  transition: transform 0.2s;

  &:hover {
    transform: translateX(2px);
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${props => props.theme.spacing(1)};
`;

const DirectionIndicator = styled.div`
  display: flex;
  align-items: center;
  color: ${props => props.theme.colors.primary};
  font-weight: ${props => props.theme.typography.fontWeight.medium};
  
  svg {
    margin-right: ${props => props.theme.spacing(1)};
  }
`;

const StatusBadge = styled.span`
  background-color: ${props => {
    switch (props.status) {
      case 'completed': return props.theme.colors.success + '22';
      case 'no-answer': 
      case 'busy': 
      case 'failed': return props.theme.colors.error + '22';
      default: return props.theme.colors.primary + '22';
    }
  }};
  color: ${props => {
    switch (props.status) {
      case 'completed': return props.theme.colors.success;
      case 'no-answer': 
      case 'busy': 
      case 'failed': return props.theme.colors.error;
      default: return props.theme.colors.primary;
    }
  }};
  padding: ${props => props.theme.spacing(0.5)} ${props => props.theme.spacing(1)};
  border-radius: 12px;
  font-size: 0.8rem;
`;

const Details = styled.div`
  margin: ${props => props.theme.spacing(1)} 0;
  font-size: 0.9rem;
  color: ${props => props.theme.colors.textSecondary};
`;

const Detail = styled.div`
  margin-bottom: ${props => props.theme.spacing(0.5)};
  display: flex;
  align-items: center;
`;

const AudioPlayer = styled.div`
  margin-top: ${props => props.theme.spacing(2)};
  width: 100%;
  
  audio {
    width: 100%;
  }
`;

const NotesSection = styled.div`
  margin-top: ${props => props.theme.spacing(2)};
  padding-top: ${props => props.theme.spacing(2)};
  border-top: 1px solid ${props => props.theme.colors.inputGray}22;
`;

const NoteText = styled.div`
  color: ${props => props.theme.colors.textPrimary};
  font-size: 0.9rem;
  white-space: pre-wrap;
`;

const NoteEditor = styled.textarea`
  width: 100%;
  padding: ${props => props.theme.spacing(1)};
  border: 1px solid ${props => props.theme.colors.inputGray};
  border-radius: ${props => props.theme.borderRadius};
  background-color: ${props => props.theme.colors.background};
  color: ${props => props.theme.colors.textPrimary};
  font-size: 0.9rem;
  min-height: 60px;
  margin-bottom: ${props => props.theme.spacing(1)};
`;

const NotesActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${props => props.theme.spacing(1)};
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing(0.5)};
  padding: ${props => props.theme.spacing(0.5)} ${props => props.theme.spacing(1)};
  border: none;
  border-radius: ${props => props.theme.borderRadius};
  background-color: ${props => 
    props.variant === 'primary' ? props.theme.colors.primary : 
    props.variant === 'danger' ? props.theme.colors.error : 
    props.theme.colors.surface};
  color: ${props => 
    props.variant === 'primary' || props.variant === 'danger' ? 
    props.theme.colors.textPrimary : props.theme.colors.textPrimary};
  cursor: pointer;
  font-size: 0.8rem;
  
  &:hover {
    opacity: 0.9;
  }
`;

const getFullRecordingUrl = (recordingUrl) => {
  return `https://api.mrpatchasphalt.com${recordingUrl}`;
};

const formatDuration = (seconds) => {
  if (!seconds) return 'N/A';
  const minutes = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${minutes}:${secs.toString().padStart(2, '0')}`;
};

const CallItem = ({ call, refreshHistory }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [editingNotes, setEditingNotes] = useState(false);
  const [notes, setNotes] = useState(call.notes || '');

  const saveNotes = async () => {
    try {
      await updateCallNotes(call.id, notes);
      setEditingNotes(false);
      if (refreshHistory) refreshHistory();
    } catch (error) {
      console.error('Failed to update notes:', error);
      // Could add error notification here
    }
  };

  return (
    <ItemContainer>
      <Header>
        <DirectionIndicator>
          {call.direction === 'inbound' ? (
            <>
              <FaLongArrowAltLeft />
              Incoming Call
            </>
          ) : (
            <>
              <FaLongArrowAltRight />
              Outgoing Call
            </>
          )}
        </DirectionIndicator>
        <StatusBadge status={call.status}>
          {call.status.charAt(0).toUpperCase() + call.status.slice(1)}
        </StatusBadge>
      </Header>

      <Details>
        <Detail>
          <strong>Time:</strong>&nbsp;{formatDateTime(call.timestamp)}
        </Detail>
        <Detail>
          <strong>From:</strong> {call.from}
        </Detail>
        <Detail>
          <strong>To:</strong> {call.to}
        </Detail>
        {call.duration > 0 && (
          <Detail>
            <strong>Duration:</strong> {formatDuration(call.duration)}
          </Detail>
        )}
      </Details>

      {call.recordingUrl && (
        <AudioPlayer>
          <audio
            controls
            src={getFullRecordingUrl(call.recordingUrl)}
            onPlay={() => setIsPlaying(true)}
            onPause={() => setIsPlaying(false)}
            onEnded={() => setIsPlaying(false)}
          />
        </AudioPlayer>
      )}

      {call.recordingUrl && (
        <TranscriptionSection
          callId={call.id}
          transcription={call.transcription}
          transcriptionStatus={call.transcription_status}
          transcribedAt={call.transcribed_at}
        />
      )}

      <NotesSection>
        {editingNotes ? (
          <>
            <NoteEditor
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Enter notes about this call..."
            />
            <NotesActions>
              <ActionButton 
                variant="danger" 
                onClick={() => {
                  setNotes(call.notes || '');
                  setEditingNotes(false);
                }}
              >
                <FaTimes /> Cancel
              </ActionButton>
              <ActionButton variant="primary" onClick={saveNotes}>
                <FaSave /> Save
              </ActionButton>
            </NotesActions>
          </>
        ) : (
          <>
            {call.notes ? (
              <>
                <NoteText>{call.notes}</NoteText>
                <NotesActions>
                  <ActionButton onClick={() => setEditingNotes(true)}>
                    <FaEdit /> Edit Notes
                  </ActionButton>
                </NotesActions>
              </>
            ) : (
              <NotesActions>
                <ActionButton onClick={() => setEditingNotes(true)}>
                  <FaEdit /> Add Notes
                </ActionButton>
              </NotesActions>
            )}
          </>
        )}
      </NotesSection>
    </ItemContainer>
  );
};

export default CallItem;